import { getUsuarioLogadoAdaptado as getUsuarioLogado } from "../../utils/ids";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatHeaderDate } from "../../utils/formatting";
import { ocorrenciasCalendario } from "../../utils/eventosCalendario";
import {
  recarregarSeNecessario,
  marcarCalendarioVisto,
  desmarcarCalendarioVisto,
  confirmarPresenca,
  desconfirmarPresenca,
} from "../../services/eventosStore";
import "./DetalheCalendarioAluno.scss";
import { ArrowLeft } from "lucide-react";

function parseISODateOnly(value) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function lerMostrarSecundarias() {
  return localStorage.getItem("menuAlunoSecundariasVisiveis") === "true";
}

export default function DetalheCalendarioAluno() {
  const navigate = useNavigate();
  const { data } = useParams();

  const [dataVersion, setDataVersion] = useState(0);
  const [mostrarSecundarias, setMostrarSecundarias] = useState(
    lerMostrarSecundarias,
  );

  useEffect(() => {
    recarregarSeNecessario().catch(() => {});

    const bump = () => setDataVersion((v) => v + 1);
    const syncSecundarias = () => {
      setMostrarSecundarias(lerMostrarSecundarias());
      bump();
    };

    window.addEventListener("app:data-changed", bump);
    window.addEventListener("eventos:changed", bump);
    window.addEventListener("menuAlunoSecundarias:changed", syncSecundarias);

    return () => {
      window.removeEventListener("app:data-changed", bump);
      window.removeEventListener("eventos:changed", bump);
      window.removeEventListener(
        "menuAlunoSecundarias:changed",
        syncSecundarias,
      );
    };
  }, []);

  const usuarioLogado = useMemo(() => {
    if (dataVersion < 0) return null;
    return getUsuarioLogado();
  }, [dataVersion]);

  const dataSelecionada = useMemo(() => parseISODateOnly(data), [data]);
  const dateKey = useMemo(
    () => (dataSelecionada ? toDateKey(dataSelecionada) : ""),
    [dataSelecionada],
  );

  // Ocorrências (evento×turma) só do dia selecionado. Não vistos primeiro.
  const eventosDaData = useMemo(() => {
    return ocorrenciasCalendario(usuarioLogado, { mostrarSecundarias })
      .filter((oc) => oc.data && oc.data.slice(0, 10) === dateKey)
      .map((oc) => ({ ...oc, __uniqueKey: `${oc.eventoId}-${oc.turmaId}` }))
      .sort((a, b) => {
        if (a.vistoCalendario !== b.vistoCalendario) {
          return a.vistoCalendario ? 1 : -1;
        }
        const da = new Date(a.ultimaAtualizacao || 0).getTime();
        const db = new Date(b.ultimaAtualizacao || 0).getTime();
        return db - da;
      });
    // usuarioLogado muda de referência a cada bump do store.
  }, [usuarioLogado, dateKey, mostrarSecundarias]);

  // Visto do calendário é reversível: clicar marca; clicar de novo desmarca,
  // voltando o evento ao estado inicial. Tudo por turma/usuário.
  async function handleToggleVisto(eventoId, turmaId, jaVisto) {
    try {
      if (jaVisto) await desmarcarCalendarioVisto(eventoId, turmaId);
      else await marcarCalendarioVisto(eventoId, turmaId);
    } catch {
      // falha de rede não trava a UI; reconcilia no próximo carregamento
    }
  }

  // Presença é independente do visto: confirmar não marca como visto e
  // vice-versa. Reconfirmar em outro dia atualiza a data no backend.
  async function handleTogglePresenca(eventoId, turmaId, jaConfirmou) {
    try {
      if (jaConfirmou) await desconfirmarPresenca(eventoId, turmaId);
      else await confirmarPresenca(eventoId, turmaId);
    } catch {
      // falha de rede não trava a UI; reconcilia no próximo carregamento
    }
  }

  // Passada a data do evento o estado congela: o botão vira somente leitura.
  const prazoEncerrado = useMemo(() => {
    if (!dataSelecionada) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return dataSelecionada.getTime() < hoje.getTime();
  }, [dataSelecionada]);

  if (!dataSelecionada) {
    return (
      <div className="detalhe-calendario-aluno-page">
        <div className="detalhe-calendario-aluno detalhe-calendario-aluno--empty">
          Data inválida.
        </div>
      </div>
    );
  }

  return (
    <div className="detalhe-calendario-aluno-page">
      <div className="detalhe-calendario-aluno">
        <div className="detalhe-calendario-aluno__topbar">
          <button
            type="button"
            className="detalhe-calendario-aluno__back"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft size={16} strokeWidth={3} />
            <span className="detalhe-calendario-aluno__backText">Voltar</span>
          </button>

          <h1 className="detalhe-calendario-aluno__title">
            {formatHeaderDate(dataSelecionada)}
          </h1>
        </div>

        <div className="detalhe-calendario-aluno__divider" />

        <div className="detalhe-calendario-aluno__list">
          {eventosDaData.length === 0 ? (
            <div className="detalhe-calendario-aluno__empty">
              Nenhum evento encontrado para esta data.
            </div>
          ) : (
            eventosDaData.map((evento) => (
              <article
                key={evento.__uniqueKey}
                className={`evento-card-aluno ${
                  evento.vistoCalendario ? "evento-card-aluno--visto" : ""
                }`}
              >
                <div className="evento-card-aluno__header">
                  <div className="evento-card-aluno__titleWrap">
                    <h2 className="evento-card-aluno__title">
                      {evento.titulo}
                    </h2>

                    <span
                      className="evento-card-aluno__badge"
                      style={{ backgroundColor: evento.cor }}
                    >
                      {evento.turmaNome}
                    </span>
                  </div>

                  <p className="evento-card-aluno__author">
                    <strong>Criado por:</strong>{" "}
                    {evento.criadoPorNome || "Não informado"}
                  </p>
                </div>

                <p className="evento-card-aluno__description">
                  {evento.descricao}
                </p>

                <div className="evento-card-aluno__footer">
                  <button
                    type="button"
                    className={`evento-card-aluno__action ${
                      evento.vistoCalendario
                        ? "evento-card-aluno__action--done"
                        : "evento-card-aluno__action--ghost"
                    }`}
                    onClick={() =>
                      handleToggleVisto(
                        evento.eventoId,
                        evento.turmaId,
                        evento.vistoCalendario,
                      )
                    }
                  >
                    ✓ {evento.vistoCalendario ? "Visto" : "Marcar como visto"}
                  </button>

                  {evento.confirmacaoPresenca && (
                    <button
                      type="button"
                      className={`evento-card-aluno__action ${
                        evento.presencaConfirmadaEm
                          ? "evento-card-aluno__action--done"
                          : "evento-card-aluno__action--ghost"
                      }`}
                      disabled={prazoEncerrado}
                      onClick={() =>
                        handleTogglePresenca(
                          evento.eventoId,
                          evento.turmaId,
                          !!evento.presencaConfirmadaEm,
                        )
                      }
                    >
                      {evento.presencaConfirmadaEm
                        ? "Presença confirmada"
                        : "Confirmar presença"}
                    </button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
